import React from 'react';

/* The model answers in light markdown (tables, lists, bold). This renders the
   small subset it uses as React elements, never as HTML, so nothing in a reply
   can inject markup. Anything unrecognised falls through as plain text. */

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g;

const renderInline = (text) => text.split(INLINE).filter(Boolean).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) return <code key={i}>{part.slice(1, -1)}</code>;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
    return part;
});

const splitRow = (line) => {
    const cells = line.trim().split('|').map((cell) => cell.trim());
    if (cells[0] === '') cells.shift();
    if (cells[cells.length - 1] === '') cells.pop();
    return cells;
};

const parseBlocks = (content) => {
    const lines = String(content).replace(/\r\n?/g, '\n').split('\n');
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (!line.trim()) { i += 1; continue; }

        if (TABLE_ROW.test(line) && TABLE_DIVIDER.test(lines[i + 1] || '')) {
            const head = splitRow(line);
            const rows = [];
            i += 2;
            while (i < lines.length && TABLE_ROW.test(lines[i])) { rows.push(splitRow(lines[i])); i += 1; }
            blocks.push({ type: 'table', head, rows });
            continue;
        }

        const list = BULLET.test(line) ? BULLET : NUMBERED.test(line) ? NUMBERED : null;
        if (list) {
            const items = [];
            while (i < lines.length && list.test(lines[i])) { items.push(lines[i].match(list)[1]); i += 1; }
            blocks.push({ type: list === BULLET ? 'ul' : 'ol', items });
            continue;
        }

        if (HEADING.test(line)) {
            blocks.push({ type: 'heading', text: line.match(HEADING)[1] });
            i += 1;
            continue;
        }

        const para = [];
        while (
            i < lines.length && lines[i].trim()
            && !(TABLE_ROW.test(lines[i]) && TABLE_DIVIDER.test(lines[i + 1] || ''))
            && !BULLET.test(lines[i]) && !NUMBERED.test(lines[i]) && !HEADING.test(lines[i])
        ) { para.push(lines[i].trim()); i += 1; }
        blocks.push({ type: 'p', lines: para });
    }
    return blocks;
};

const renderBlock = (block, key) => {
    switch (block.type) {
        case 'table':
            return (
                <div key={key} className="assistant-md-table">
                    <table>
                        <thead><tr>{block.head.map((cell, c) => <th key={c}>{renderInline(cell)}</th>)}</tr></thead>
                        <tbody>
                            {block.rows.map((row, r) => (
                                <tr key={r}>{block.head.map((_, c) => <td key={c}>{renderInline(row[c] || '')}</td>)}</tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        case 'ul':
        case 'ol': {
            const List = block.type;
            return <List key={key}>{block.items.map((item, n) => <li key={n}>{renderInline(item)}</li>)}</List>;
        }
        case 'heading':
            return <p key={key} className="assistant-md-heading">{renderInline(block.text)}</p>;
        default:
            return (
                <p key={key}>
                    {block.lines.map((text, n) => (
                        <React.Fragment key={n}>{n > 0 && <br />}{renderInline(text)}</React.Fragment>
                    ))}
                </p>
            );
    }
};

const AssistantMarkdown = ({ content }) => (
    <div className="assistant-md">{parseBlocks(content).map(renderBlock)}</div>
);

export default AssistantMarkdown;
